import React, { ComponentType } from 'react'
import {
  pickBy,
  isPlainObject,
  values,
  reduce,
  isEmpty,
  mapValues,
  isArray,
  get,
} from 'lodash'
import { TFunction } from 'react-i18next'

import { CONTROL } from '../Constants'
import featureMappings from './FeatureMappings'
import { Feature } from '../Features'

const processFeature = (feature: Feature) => {
  const { control, ...props } = feature
  const subFeatures = values(pickBy(feature, (value) => isPlainObject(value)))
  return { control, props, subFeatures }
}

const buildUIComponent = (
  control: keyof typeof featureMappings,
  options: Record<string, any>,
): JSX.Element | null => {
  const mapping = featureMappings[control]
  if (!mapping) {
    console.error(`No mapping found for type: ${control}`)
    return null
  }

  const Component: ComponentType<any> = mapping.component
  const combinedProps = { ...mapping.props, ...options }
  return (
    <Component key={options.id} name={combinedProps.id} {...combinedProps} />
  )
}

const handleOptionGroup = (
  control: keyof typeof featureMappings,
  props: { [key: string]: any },
  options: Record<string, any>,
) => {
  const { options: inputOptions, ...rest } = props
  const conditionalFeatures = reduce(
    inputOptions,
    (acc, item) => {
      const subFeature = pickBy(item, isPlainObject)

      if (subFeature && !isEmpty(subFeature)) {
        return { ...acc, [item.id]: values(subFeature) }
      }
      return acc
    },
    {},
  )
  return buildUIComponent(control, {
    ...rest,
    ...options,
    options: inputOptions,
    renderConditionalComponents: (option: string, props: any) =>
      ((get(conditionalFeatures, option) as Feature[]) || []).map(
        (feature: Feature) => {
          const { control, props: featureProps } = processFeature(feature)
          return buildUIComponent(control, { ...featureProps, ...props })
        },
      ),
  })
}

const renderUIField = (
  feature: Feature,
  options: Record<string, any> = {},
): JSX.Element | null => {
  const { control, props, subFeatures } = processFeature(feature)

  if (control === CONTROL.GROUP) {
    const subUi = subFeatures.map((subFeature: Feature) => {
      const { props: subProps } = processFeature(subFeature)
      return renderUIField(subFeature, subProps)
    })

    return buildUIComponent(control, { ...options, ...props, children: subUi })
  } else if (
    control === CONTROL.RADIO_GROUP ||
    control === CONTROL.CHECKBOX_GROUP
  ) {
    return handleOptionGroup(control, props, options)
  }

  return buildUIComponent(control, { ...props, ...options })
}

const translateFeature = (obj: Feature, translate: TFunction) => {
  const traverse = (obj: Feature): Feature => {
    return mapValues(obj, (value) => {
      if (isPlainObject(value)) {
        return traverse(value)
      }
      if (isArray(value)) {
        return value.map((item) => traverse(item))
      }
      if (
        typeof value === 'string' &&
        /^[a-zA-Z0-9]+\.[a-zA-Z0-9.]+$/.test(value)
      ) {
        return translate(value)
      }
      return value
    })
  }

  return traverse(obj)
}

export { renderUIField, translateFeature }
